/**
 * Client-side menu parsers for CSV, PDF text, and HTML/URL content.
 * Extracts items with name, optional ingredients, and price.
 */

import { enrichSpaMenuHtml } from "./spaMenuExtract";

const SECTION_ONLY_RE =
  /^(packages?|appetizers?|entrees?|entrées?|desserts?|drinks?|beverages?|sides?|lunch|dinner|breakfast|catering|menu|category|section)$/i;

function normalizeWhitespace(text) {
  return String(text || "")
    .replace(/\r\n/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+/g, " ")
    .trim();
}

function parseSinglePrice(raw) {
  if (raw == null || raw === "") return null;
  const text = String(raw).trim();
  const dollar = text.match(/\$\s*(\d{1,4}(?:[.,]\d{2})?)/);
  if (dollar) {
    const num = Number.parseFloat(dollar[1].replace(",", "."));
    return Number.isFinite(num) ? Number(num.toFixed(2)) : null;
  }
  // Only accept a single standalone amount (never digit-concatenated values like 129249)
  const plain = text.match(/^(\d{1,4}(?:[.,]\d{2})?)$/);
  if (plain) {
    const num = Number.parseFloat(plain[1].replace(",", "."));
    return Number.isFinite(num) ? Number(num.toFixed(2)) : null;
  }
  return null;
}

function cleanPrice(raw) {
  if (raw == null || raw === "") return null;
  const dollars = extractDollarPrices(raw);
  if (dollars.length) return dollars[0];
  return parseSinglePrice(raw);
}

function extractDollarPrices(text) {
  const prices = [];
  const src = String(text || "");
  let match;
  // Matches $129, $129.00, and glued pairs like $129$249 as two prices
  const re = /\$\s*(\d{1,4}(?:[.,]\d{2})?)/g;
  while ((match = re.exec(src)) !== null) {
    const num = Number.parseFloat(match[1].replace(",", "."));
    if (Number.isFinite(num)) prices.push(Number(num.toFixed(2)));
  }
  return prices;
}

/** Split a cell that accidentally merged two prices, e.g. "$129$249" or "$129 $249". */
function splitMergedPrices(text) {
  const dollars = extractDollarPrices(text);
  if (dollars.length >= 2) return dollars;
  return dollars.length === 1 ? dollars : [];
}

function stripHtml(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script, style, noscript").forEach((el) => el.remove());
  return normalizeWhitespace(doc.body?.textContent || "");
}

function parseCsvLine(line) {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      cells.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  cells.push(current.trim());
  return cells;
}

function detectCsvColumns(headerCells) {
  const normalized = headerCells.map((h) =>
    h.toLowerCase().replace(/[^a-z0-9]/g, "")
  );
  const find = (...aliases) =>
    normalized.findIndex((h) => aliases.some((a) => h.includes(a)));

  const name = find("name", "item", "dish", "title", "menuitem");
  const ingredients = find("ingredient", "desc", "description", "detail");
  let price = find("price", "cost", "amount");
  const serves10 = find("serves10", "serve10", "for10");
  const serves20 = find("serves20", "serve20", "for20");

  if (price < 0 && serves10 >= 0) price = serves10;
  if (price < 0 && serves20 >= 0) price = serves20;

  return { name, ingredients, price, serves10, serves20 };
}

function toMenuItem({ name, ingredients, price, prices }) {
  const primary =
    price != null
      ? price
      : prices?.serves10 != null
        ? prices.serves10
        : prices?.serves20 != null
          ? prices.serves20
          : null;

  const item = {
    name: normalizeWhitespace(name),
    ingredients: ingredients ? normalizeWhitespace(ingredients) : null,
    price: primary,
  };

  if (prices && (prices.serves10 != null || prices.serves20 != null)) {
    item.prices = {
      ...(prices.serves10 != null ? { serves10: prices.serves10 } : {}),
      ...(prices.serves20 != null ? { serves20: prices.serves20 } : {}),
    };
  }

  return item;
}

export function parseCsvMenu(csvText) {
  const lines = String(csvText || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (!lines.length) return [];

  const first = parseCsvLine(lines[0]);
  const looksLikeHeader = first.some((c) =>
    /name|item|price|ingredient|desc|serves/i.test(c)
  );

  let columns;
  let startIndex = 0;

  if (looksLikeHeader) {
    columns = detectCsvColumns(first);
    startIndex = 1;
    if (columns.name < 0) columns.name = 0;
    if (columns.ingredients < 0) {
      columns.ingredients = first.length > 2 ? 1 : -1;
    }
    if (columns.price < 0) {
      columns.price = Math.min(first.length - 1, 2);
    }
  } else {
    columns = {
      name: 0,
      ingredients: first.length > 2 ? 1 : -1,
      price: first.length > 1 ? first.length - 1 : -1,
      serves10: -1,
      serves20: -1,
    };
  }

  const items = [];
  for (let i = startIndex; i < lines.length; i += 1) {
    const cells = parseCsvLine(lines[i]);
    let name = cells[columns.name] || "";
    if (!name || SECTION_ONLY_RE.test(name)) continue;

    const ingredients =
      columns.ingredients >= 0 ? cells[columns.ingredients] || "" : "";

    const prices = {};
    if (columns.serves10 >= 0) {
      prices.serves10 = cleanPrice(cells[columns.serves10]);
    }
    if (columns.serves20 >= 0) {
      prices.serves20 = cleanPrice(cells[columns.serves20]);
    }

    let price =
      columns.price >= 0 ? cleanPrice(cells[columns.price]) : null;
    if (price == null) {
      const fromRow = extractDollarPrices(cells.join(" "));
      price = fromRow[0] ?? null;
    }

    name = name.replace(/\$\s*\d+(?:[.,]\d{2})?/g, "").trim() || name;

    items.push(
      toMenuItem({
        name,
        ingredients,
        price,
        prices:
          prices.serves10 != null || prices.serves20 != null ? prices : null,
      })
    );
  }

  return items;
}

/**
 * Heuristic line parser for free-form menu text (PDF / HTML).
 */
export function parseMenuText(rawText) {
  const text = normalizeWhitespace(rawText);
  if (!text) return [];

  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 2);

  const items = [];
  const seen = new Set();

  for (const line of lines) {
    if (
      /^(menu|appetizer|entree|entrée|dessert|drink|beverage|side|lunch|dinner|breakfast|category|section|packages?|item|ingredients?|serves)\b/i.test(
        line
      ) &&
      !/\$\s*\d/.test(line)
    ) {
      continue;
    }

    const dollarPrices = extractDollarPrices(line);
    if (!dollarPrices.length) continue;

    let left = line
      .replace(/\$\s*\d{1,4}(?:[.,]\d{2})?/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (!left || left.length > 220) continue;
    if (/^(subtotal|total|tax|tip|page\s*\d)/i.test(left)) continue;

    let name = left;
    let ingredients = null;

    // "Name - ingredients" or "Name: ingredients" or "Name | ingredients"
    const sep = left.match(/^(.+?)\s*[-–—|:]\s+(.+)$/);
    if (sep && sep[2].length > 2 && !/^\d/.test(sep[2])) {
      name = sep[1].trim();
      ingredients = sep[2].trim();
    } else {
      // Table-like: "Breakfast Package Scrambled eggs, home fries..."
      // Split after Package / known dish endings before a lowercase ingredient list
      const packageSplit = left.match(
        /^(.+?\b(?:package|platter|combo|meal|box|tray))\s+([a-z].+)$/i
      );
      if (packageSplit) {
        name = packageSplit[1].trim();
        ingredients = packageSplit[2].trim();
      } else {
        const paren = left.match(/^(.+?)\s*\((.+)\)\s*$/);
        if (paren) {
          name = paren[1].trim();
          ingredients = paren[2].trim();
        }
      }
    }

    name = name.replace(/\.+$/, "").trim();
    if (name.length < 2 || SECTION_ONLY_RE.test(name)) continue;

    const price = dollarPrices[0];
    const key = `${name.toLowerCase()}|${price}`;
    if (seen.has(key)) continue;
    seen.add(key);

    items.push(
      toMenuItem({
        name,
        ingredients,
        price,
        prices:
          dollarPrices.length > 1
            ? { serves10: dollarPrices[0], serves20: dollarPrices[1] }
            : null,
      })
    );
  }

  return items;
}

/**
 * Build rows of text cells from PDF.js text items using x/y positions.
 * This preserves ITEM | INGREDIENTS | PRICE column structure.
 */
function buildPdfTableRows(textItems) {
  const tokens = textItems
    .map((item) => {
      const str = (item.str || "").trim();
      if (!str) return null;
      const x = item.transform?.[4] ?? 0;
      const y = item.transform?.[5] ?? 0;
      const width = item.width || str.length * 5;
      return { str, x, y, width, xEnd: x + width };
    })
    .filter(Boolean)
    .sort((a, b) => b.y - a.y || a.x - b.x);

  if (!tokens.length) return [];

  const yGaps = [];
  for (let i = 1; i < Math.min(tokens.length, 80); i += 1) {
    yGaps.push(Math.abs(tokens[i - 1].y - tokens[i].y));
  }
  const nonzero = yGaps.filter((g) => g > 0.5).sort((a, b) => a - b);
  const medianGap = nonzero[Math.floor(nonzero.length / 2)] || 10;
  const yTolerance = Math.max(4, Math.min(14, medianGap * 0.45));

  const rows = [];
  let current = [];
  let currentY = null;

  tokens.forEach((token) => {
    if (currentY == null || Math.abs(token.y - currentY) <= yTolerance) {
      current.push(token);
      currentY =
        currentY == null
          ? token.y
          : (currentY * (current.length - 1) + token.y) / current.length;
    } else {
      rows.push(mergeRowTokens(current));
      current = [token];
      currentY = token.y;
    }
  });
  if (current.length) rows.push(mergeRowTokens(current));

  return rows;
}

function mergeRowTokens(tokens) {
  const sorted = [...tokens].sort((a, b) => a.x - b.x);
  const cells = [];
  let buf = null;

  sorted.forEach((token) => {
    if (!buf) {
      buf = { ...token };
      return;
    }
    const gap = token.x - buf.xEnd;
    const bufIsPrice = /^\$?\s*\d{1,4}(?:[.,]\d{2})?$/.test(buf.str.trim());
    const tokenIsPrice = /^\$?\s*\d{1,4}(?:[.,]\d{2})?$/.test(token.str.trim());
    // Keep separate price columns from merging into "$129$249"
    if (bufIsPrice && tokenIsPrice) {
      cells.push(buf);
      buf = { ...token };
      return;
    }
    // Same cell if close; new cell if clear horizontal gap (column separation)
    if (gap < 12) {
      const needsSpace =
        buf.str &&
        token.str &&
        !/\s$/.test(buf.str) &&
        !/^[.,;:!?)]/.test(token.str);
      buf.str += (needsSpace ? " " : "") + token.str;
      buf.xEnd = Math.max(buf.xEnd, token.xEnd);
    } else {
      cells.push(buf);
      buf = { ...token };
    }
  });
  if (buf) cells.push(buf);

  return {
    y: tokens.reduce((s, t) => s + t.y, 0) / tokens.length,
    cells: cells.map((c) => ({
      text: normalizeWhitespace(c.str),
      x: c.x,
      xEnd: c.xEnd,
      mid: (c.x + c.xEnd) / 2,
    })),
    text: cells.map((c) => c.str).join(" | "),
  };
}

function detectTableColumns(rows) {
  const headerIdx = rows.findIndex((row) => {
    const joined = row.cells.map((c) => c.text.toLowerCase()).join(" ");
    return (
      /\bitem\b/.test(joined) &&
      /\bingredients?\b/.test(joined)
    );
  });

  if (headerIdx < 0) return null;

  const header = rows[headerIdx];
  const columns = header.cells.map((cell) => {
    const label = cell.text.toLowerCase().replace(/\s+/g, " ").trim();
    let role = "other";
    if (/^item$|name|dish/.test(label)) role = "name";
    else if (/ingredient|desc/.test(label)) role = "ingredients";
    else if (/serves?\s*10|for\s*10/.test(label)) role = "serves10";
    else if (/serves?\s*20|for\s*20/.test(label)) role = "serves20";
    else if (/price|cost|amount|serves?/.test(label)) role = "price";

    return {
      role,
      label: cell.text,
      x: cell.x,
      xEnd: cell.xEnd,
      mid: cell.mid,
    };
  });

  // Ensure we have name + ingredients roles when labels match expected catering layout
  if (!columns.some((c) => c.role === "name") && columns[0]) {
    columns[0].role = "name";
  }
  if (!columns.some((c) => c.role === "ingredients") && columns[1]) {
    columns[1].role = "ingredients";
  }

  return { headerIdx, columns };
}

function assignCellToColumn(cell, columns) {
  let best = 0;
  let bestDist = Infinity;
  columns.forEach((col, idx) => {
    const dist = Math.abs(cell.mid - col.mid);
    if (dist < bestDist) {
      bestDist = dist;
      best = idx;
    }
  });
  return best;
}

function parsePdfTableRows(rows) {
  const detected = detectTableColumns(rows);
  if (!detected) return [];

  const { headerIdx, columns } = detected;
  const items = [];
  let pending = null;

  for (let i = headerIdx + 1; i < rows.length; i += 1) {
    const row = rows[i];
    if (!row.cells.length) continue;

    const bucket = columns.map(() => []);
    row.cells.forEach((cell) => {
      const idx = assignCellToColumn(cell, columns);
      bucket[idx].push(cell.text);
    });

    const values = bucket.map((parts) => normalizeWhitespace(parts.join(" ")));
    const byRole = (role) => {
      const idx = columns.findIndex((c) => c.role === role);
      return idx >= 0 ? values[idx] : "";
    };

    let name = byRole("name");
    let ingredients = byRole("ingredients");
    const s10Text = byRole("serves10");
    const s20Text = byRole("serves20");
    const priceText = byRole("price");

    let serves10Prices = splitMergedPrices(s10Text);
    let serves20Prices = splitMergedPrices(s20Text);

    // If both prices landed in the Serves 10 column, split them
    if (serves10Prices.length >= 2 && serves20Prices.length === 0) {
      serves20Prices = [serves10Prices[1]];
      serves10Prices = [serves10Prices[0]];
    }

    let serves10 = serves10Prices[0] ?? parseSinglePrice(s10Text);
    let serves20 = serves20Prices[0] ?? parseSinglePrice(s20Text);
    let price = cleanPrice(priceText);

    const allText = values.join(" ");
    let dollars = extractDollarPrices(allText);

    // Prefer left-to-right price cells when column mapping still failed
    if (serves10 == null || serves20 == null) {
      const priceCells = row.cells
        .filter((c) => /\$\s*\d/.test(c.text))
        .sort((a, b) => a.x - b.x);
      const fromCells = priceCells.flatMap((c) => extractDollarPrices(c.text));
      if (fromCells.length) dollars = fromCells;
      if (serves10 == null && dollars[0] != null) serves10 = dollars[0];
      if (serves20 == null && dollars[1] != null) serves20 = dollars[1];
    }

    // Never keep a concatenated artifact (e.g. 129249)
    if (serves10 != null && serves10 >= 10000) serves10 = dollars[0] ?? null;
    if (serves20 != null && serves20 >= 10000) serves20 = dollars[1] ?? null;

    // Section banner like "PACKAGES"
    if (
      SECTION_ONLY_RE.test(name) ||
      (row.cells.length === 1 && SECTION_ONLY_RE.test(row.cells[0].text))
    ) {
      pending = null;
      continue;
    }

    // Continuation row: ingredients wrapped onto next line
    const hasPrice = serves10 != null || serves20 != null || price != null || dollars.length > 0;
    const looksLikeName =
      name &&
      !/^\$/.test(name) &&
      name.length < 80 &&
      !/,/.test(name.slice(0, 40));

    if (!looksLikeName && !hasPrice && ingredients && pending) {
      pending.ingredients = normalizeWhitespace(
        `${pending.ingredients || ""} ${ingredients || name}`.trim()
      );
      continue;
    }

    if (!looksLikeName && hasPrice && pending && !name) {
      // price-only continuation; ignore
      continue;
    }

    if (!looksLikeName) {
      // Fallback: first non-price cell is name, next long cell ingredients
      const nonPrice = row.cells
        .map((c) => c.text)
        .filter((t) => !/^\$?\d/.test(t) && !SECTION_ONLY_RE.test(t));
      if (nonPrice.length >= 2) {
        name = nonPrice[0];
        ingredients = nonPrice.slice(1).join(" ");
      } else if (nonPrice.length === 1 && hasPrice) {
        name = nonPrice[0];
      } else {
        continue;
      }
    }

    if (!name || SECTION_ONLY_RE.test(name)) continue;

    if (price == null) price = dollars[0] ?? serves10 ?? serves20 ?? null;
    const prices = {};
    if (serves10 != null) prices.serves10 = serves10;
    else if (dollars[0] != null && dollars[1] != null) prices.serves10 = dollars[0];
    if (serves20 != null) prices.serves20 = serves20;
    else if (dollars[1] != null) prices.serves20 = dollars[1];

    // If ingredients empty but name absorbed description, try split
    if (!ingredients) {
      const packageSplit = name.match(
        /^(.+?\b(?:package|platter|combo|meal|box|tray))\s+([a-z].+)$/i
      );
      if (packageSplit) {
        ingredients = packageSplit[2];
        name = packageSplit[1];
      }
    }

    const item = toMenuItem({
      name,
      ingredients,
      price,
      prices:
        prices.serves10 != null || prices.serves20 != null ? prices : null,
    });

    items.push(item);
    pending = item;
  }

  return items;
}

export async function extractPdfText(file) {
  const { text } = await extractPdfContent(file);
  return text;
}

export async function extractPdfContent(file) {
  const pdfjsLib = await import("pdfjs-dist/build/pdf");
  const pdfjs = pdfjsLib.default || pdfjsLib;
  const workerModule = await import("pdfjs-dist/build/pdf.worker.entry");
  pdfjs.GlobalWorkerOptions.workerSrc = workerModule.default || workerModule;

  const data = new Uint8Array(await file.arrayBuffer());
  const pdf = await pdfjs.getDocument({ data }).promise;
  const pageTexts = [];
  const allRows = [];

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
    const page = await pdf.getPage(pageNum);
    const content = await page.getTextContent();
    const rows = buildPdfTableRows(content.items || []);
    allRows.push(...rows);
    pageTexts.push(rows.map((r) => r.cells.map((c) => c.text).join("\t")).join("\n"));
  }

  return {
    text: pageTexts.join("\n"),
    rows: allRows,
  };
}

export async function parsePdfMenu(file) {
  const { text, rows } = await extractPdfContent(file);
  const tableItems = parsePdfTableRows(rows);
  if (tableItems.length) return tableItems;

  // Fallback for non-tabular PDFs
  return parseMenuText(text);
}

function parseOrderByteMenuCards(doc) {
  const items = [];
  const seen = new Set();
  const cards = doc.querySelectorAll(
    ".card-dish .item, .popular-item, .item.popular-item"
  );

  cards.forEach((card) => {
    const heading = card.querySelector(".heading h3, .top h3, h3");
    if (!heading) return;
    const name = normalizeWhitespace(heading.textContent || "");
    if (!name || name.length < 2 || SECTION_ONLY_RE.test(name)) return;

    const descEl = card.querySelector(".desc p, .desc");
    const ingredients = descEl
      ? normalizeWhitespace(descEl.textContent || "")
      : null;

    const priceEl = card.querySelector(".link-btn h5, .bottom h5");
    const price = cleanPrice(priceEl?.textContent || "");
    if (price == null) return;

    const key = `${name.toLowerCase()}|${price}`;
    if (seen.has(key)) return;
    seen.add(key);

    items.push(
      toMenuItem({
        name,
        ingredients: ingredients && ingredients !== name ? ingredients : null,
        price,
      })
    );
  });

  return items;
}

export function parseHtmlMenu(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");

  const orderCards = parseOrderByteMenuCards(doc);
  if (orderCards.length) return orderCards;

  // Prefer HTML tables when present
  const tables = [...doc.querySelectorAll("table")];
  for (const table of tables) {
    const items = parseHtmlTable(table);
    if (items.length) return items;
  }
  return parseMenuText(stripHtml(html));
}

function parseHtmlTable(table) {
  const rows = [...table.querySelectorAll("tr")].map((tr) =>
    [...tr.querySelectorAll("th,td")].map((td) =>
      normalizeWhitespace(td.textContent || "")
    )
  );
  if (rows.length < 2) return [];

  const headerIdx = rows.findIndex(
    (r) =>
      r.some((c) => /^item$/i.test(c)) &&
      r.some((c) => /ingredient/i.test(c))
  );
  if (headerIdx < 0) {
    // Generic: assume col0 name, col1 ingredients, last cols prices
    return rows.slice(1).flatMap((cells) => {
      if (cells.length < 2) return [];
      const name = cells[0];
      if (!name || SECTION_ONLY_RE.test(name)) return [];
      const dollars = extractDollarPrices(cells.join(" "));
      return [
        toMenuItem({
          name,
          ingredients: cells[1] || null,
          price: dollars[0] ?? cleanPrice(cells[cells.length - 1]),
          prices:
            dollars.length > 1
              ? { serves10: dollars[0], serves20: dollars[1] }
              : null,
        }),
      ];
    });
  }

  const header = rows[headerIdx].map((h) => h.toLowerCase());
  const nameIdx = header.findIndex((h) => /item|name/.test(h));
  const ingIdx = header.findIndex((h) => /ingredient/.test(h));
  const s10 = header.findIndex((h) => /serves?\s*10/.test(h));
  const s20 = header.findIndex((h) => /serves?\s*20/.test(h));

  const items = [];
  for (let i = headerIdx + 1; i < rows.length; i += 1) {
    const cells = rows[i];
    const name = cells[nameIdx] || "";
    if (!name || SECTION_ONLY_RE.test(name)) continue;
    const ingredients = ingIdx >= 0 ? cells[ingIdx] : "";
    const prices = {
      serves10: s10 >= 0 ? cleanPrice(cells[s10]) : null,
      serves20: s20 >= 0 ? cleanPrice(cells[s20]) : null,
    };
    items.push(
      toMenuItem({
        name,
        ingredients,
        price: prices.serves10 ?? prices.serves20,
        prices:
          prices.serves10 != null || prices.serves20 != null ? prices : null,
      })
    );
  }
  return items;
}

async function fetchRemoteForSpaEnrich(remoteUrl) {
  const normalized = String(remoteUrl || "").trim();
  try {
    const localProxy = `/api/fetch-url?url=${encodeURIComponent(normalized)}`;
    const proxied = await fetch(localProxy);
    if (proxied.ok) return proxied;
  } catch (_) {
    // continue
  }
  try {
    const mirror = `https://api.allorigins.win/raw?url=${encodeURIComponent(
      normalized
    )}`;
    return await fetch(mirror);
  } catch (_) {
    return fetch(normalized, { method: "GET" });
  }
}

async function attachSpaMenuPayload(html, pageUrl) {
  const enriched = await enrichSpaMenuHtml(html, pageUrl, fetchRemoteForSpaEnrich);
  if (!enriched.enriched) {
    return { text: html, kind: "html" };
  }
  return {
    text: enriched.html,
    kind: "html",
    spaItems: enriched.items,
    enriched: true,
  };
}

const IMAGE_URL_RE = /\.(png|jpe?g|webp|gif|bmp|heic|avif)(\?|#|$)/i;

export function isImageMenuResponse(contentType, url) {
  const ct = String(contentType || "").toLowerCase();
  if (ct.startsWith("image/")) return true;
  return IMAGE_URL_RE.test(String(url || ""));
}

function imageFileFromBlob(blob, url, fallbackName = "menu") {
  const type = blob.type || "image/png";
  const extMatch = String(url || "").match(IMAGE_URL_RE);
  const ext = extMatch ? extMatch[1].replace("jpeg", "jpg") : "png";
  const name = `${fallbackName}.${ext}`;
  return new File([blob], name, { type });
}

export async function fetchMenuFromUrl(url) {
  let normalized = String(url || "").trim();
  if (!/^https?:\/\//i.test(normalized)) {
    normalized = `https://${normalized}`;
  }

  const classifyResponse = async (res, sourceName = "menu") => {
    const contentType = res.headers.get("content-type") || "";
    if (
      contentType.includes("text/csv") ||
      normalized.toLowerCase().endsWith(".csv")
    ) {
      return { text: await res.text(), kind: "csv" };
    }
    if (
      contentType.includes("application/pdf") ||
      normalized.toLowerCase().endsWith(".pdf")
    ) {
      const blob = await res.blob();
      const file = new File([blob], `${sourceName}.pdf`, {
        type: "application/pdf",
      });
      return { file, kind: "pdf" };
    }
    if (isImageMenuResponse(contentType, normalized)) {
      const blob = await res.blob();
      const file = imageFileFromBlob(blob, normalized, sourceName);
      return { file, kind: "image" };
    }
    const html = await res.text();
    return attachSpaMenuPayload(html, normalized);
  };

  // 1) Same-origin CRA proxy (avoids browser CORS) — preferred in development
  try {
    const localProxy = `/api/fetch-url?url=${encodeURIComponent(normalized)}`;
    const proxied = await fetch(localProxy);
    if (proxied.ok) {
      return classifyResponse(proxied, "menu");
    }
  } catch (_) {
    // continue
  }

  // 2) Direct browser fetch (only works when the site allows CORS)
  try {
    const res = await fetch(normalized, { method: "GET" });
    if (res.ok) {
      return classifyResponse(res, "menu");
    }
  } catch (_) {
    // continue
  }

  // 3) Public CORS mirror (best-effort)
  try {
    const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(
      normalized
    )}`;
    const proxyRes = await fetch(proxyUrl);
    if (proxyRes.ok) {
      if (
        normalized.toLowerCase().endsWith(".csv") ||
        (proxyRes.headers.get("content-type") || "").includes("text/csv")
      ) {
        return { text: await proxyRes.text(), kind: "csv" };
      }
      if (
        isImageMenuResponse(proxyRes.headers.get("content-type"), normalized)
      ) {
        const blob = await proxyRes.blob();
        return {
          file: imageFileFromBlob(blob, normalized, "menu"),
          kind: "image",
        };
      }
      const text = await proxyRes.text();
      return attachSpaMenuPayload(text, normalized);
    }
  } catch (_) {
    // continue
  }

  throw new Error(
    "Could not fetch the menu link (blocked by the website or network). Upload a PDF, image, CSV, or HTML file instead."
  );
}

export function buildMenuJson({ items, source, sourceName }) {
  return {
    source,
    sourceName: sourceName || null,
    parsedAt: new Date().toISOString(),
    itemCount: items.length,
    items: items.map((item) => ({
      name: item.name,
      ingredients: item.ingredients || null,
      price: item.price,
      ...(item.productId ? { productId: item.productId } : {}),
      ...(item.modifierGroups?.length
        ? { modifierGroups: item.modifierGroups }
        : {}),
      ...(item.detailUrl ? { detailUrl: item.detailUrl } : {}),
      ...(item.fetchSource ? { fetchSource: item.fetchSource } : {}),
      ...(item.prices ? { prices: item.prices } : {}),
    })),
  };
}
